import { Button } from '@components/base';
import {
  DEFAULT_CUSTOM_CLEARANCE_PERCENTAGE,
  DEFAULT_CUSTOM_ISF_FILLING_CHARGES,
  DEFAULT_INSURANCE_COST,
  NON_CONSOLIDATION_ENQUIRY_QUOTE_DEFAULT_VALUE,
  getCountryOfOriginOption,
  getMaterialLocationCountryOption,
  toQuoteProductOption
} from '@components/common/quote-wrapper/components/helper';
import { FormProvider, useFieldArray, useForm, useWatch } from 'react-hook-form';
import css from './index.module.scss';
import { CURRENCY, OPERATION_TYPE, QUOTE_CONTAINER_TYPE } from '@helpers/constants';
import { useEffect } from 'react';
import { IFilterState, IWeightChildOption } from '@helpers/types/quotes';
import CostCalculator from '@components/common/quote-wrapper/components/cost-calculator';
import { yupResolver } from '@hookform/resolvers/yup';
import { nonConsolidationAddEditQuoteValidation } from '@helpers/yup/add-quote.schema';
import NonConsolidationQuote from '@components/common/quote-wrapper/components/international-non-consolidation/components/non-consolidation';
import { notifyFirstFormError } from '@helpers/utils';

interface INonConsolidatedProps {
  quoteFormState: any;
  filterState: IFilterState;
  filterProductOptions: (event: any, param?: any) => Promise<void>;
  filterPorts: (event?: any, param?: any, fieldType?: string) => Promise<void>;
  onClose: () => void;
  onFormSubmit: (enquiryQuote: any) => Promise<void>;
  data: any;
}

const NonConsolidated = (props: INonConsolidatedProps) => {
  const {
    quoteFormState,
    filterState,
    filterProductOptions,
    filterPorts,
    onClose,
    onFormSubmit,
    data
  } = props;

  const {
    selectedWeightSystemChildOptions,
    selectedCurrency,
    productOptions,
    portOfLoadingOptions,
    portOfDischargeOptions,
    isEdit,
    primaryUnit,
    currency1,
    fxRate
  } = quoteFormState;

  const quoteForm = useForm<any>({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    resolver: yupResolver(nonConsolidationAddEditQuoteValidation),
    defaultValues: {
      quote: [NON_CONSOLIDATION_ENQUIRY_QUOTE_DEFAULT_VALUE]
    }
  });

  const { control, handleSubmit } = quoteForm;

  const {
    fields: fields,
    append: append,
    remove: remove
  } = useFieldArray({
    name: 'quote',
    control
  });

  useEffect(() => {
    if (data) {
      const currentValues = quoteForm.getValues();

      const productName = toQuoteProductOption(data?.product_data ?? data);

      if (isEdit == OPERATION_TYPE.ADD) {
        const updatedQuote = {
          ...currentValues.quote[0],
          productName: productName
        };
        quoteForm.reset({
          quote: [updatedQuote]
        });
      } else {
        quoteForm.reset({
          quote: [
            {
              isExpanded: true,
              quote_id: data?.quote_id,
              productName: productName,
              productPackaging: data.packaging,
              dealQuantity: data?.quantity,
              dealQuantityUnit: data?.quantity_unit
                ? selectedWeightSystemChildOptions?.find(
                    (item: IWeightChildOption) => item.value === data?.quantity_unit
                  )
                : primaryUnit,
              containerSize: data?.container_size
                ? QUOTE_CONTAINER_TYPE.find((item: any) => item.value === data.container_size)
                : null,
              base_price: {
                value: data.base_price?.value,
                currency: data.base_price
                  ? CURRENCY?.find((item) => item.value === data.base_price?.currency)
                  : currency1,
                cost_id: isEdit == OPERATION_TYPE.DUBLICATE ? null : data.base_price?.cost_id,
                weightUnit: data?.base_price?.unit_of_weight
                  ? selectedWeightSystemChildOptions?.find(
                      (item: IWeightChildOption) => item.value === data?.base_price?.unit_of_weight
                    )
                  : primaryUnit
              },
              final_exWorks_price: {
                value: data.ex_works_price?.value,
                currency: data.ex_works_price
                  ? CURRENCY?.find((item) => item.value === data.ex_works_price?.currency)
                  : currency1,
                cost_id: isEdit == OPERATION_TYPE.DUBLICATE ? null : data.ex_works_price?.cost_id,
                manualOverride: true,
                weightUnit: data?.ex_works_price?.unit_of_weight
                  ? selectedWeightSystemChildOptions?.find(
                      (item: IWeightChildOption) =>
                        item.value === data?.ex_works_price?.unit_of_weight
                    )
                  : primaryUnit
              },
              fob_price: {
                value: data?.fob_price?.value,
                currency: data?.fob_price
                  ? CURRENCY?.find((item) => item.value === data?.fob_price?.currency)
                  : currency1,
                cost_id: isEdit == OPERATION_TYPE.DUBLICATE ? null : data.fob_price?.cost_id,
                manualOverride: data?.fob_price?.value ? true : false,
                weightUnit: data?.fob_price?.unit_of_weight
                  ? selectedWeightSystemChildOptions?.find(
                      (item: IWeightChildOption) => item.value === data?.fob_price?.unit_of_weight
                    )
                  : primaryUnit
              },
              ddp_price: {
                value: data?.ddp_price?.value,
                currency: data?.ddp_price
                  ? CURRENCY?.find((item) => item.value === data?.ddp_price?.currency)
                  : currency1,
                cost_id: isEdit == OPERATION_TYPE.DUBLICATE ? null : data.ddp_price?.cost_id,
                manualOverride: data?.ddp_price?.value ? true : false,
                weightUnit: data?.ddp_price?.unit_of_weight
                  ? selectedWeightSystemChildOptions?.find(
                      (item: IWeightChildOption) => item.value === data?.ddp_price?.unit_of_weight
                    )
                  : primaryUnit
              },
              drayageCharges: {
                value: data.transportation_cost?.value,
                currency: data.transportation_cost
                  ? CURRENCY?.find((item) => item.value === data.transportation_cost?.currency)
                  : currency1,
                cost_id:
                  isEdit == OPERATION_TYPE.DUBLICATE ? null : data.transportation_cost?.cost_id
              },
              customClearance: data.custom_duty ?? DEFAULT_CUSTOM_CLEARANCE_PERCENTAGE,
              isfFiling: data.isf_filing
                ? {
                    value: data.isf_filing?.value,
                    currency: data.isf_filing
                      ? CURRENCY?.find((item) => item.value === data.isf_filing?.currency)
                      : currency1,
                    cost_id: isEdit == OPERATION_TYPE.DUBLICATE ? null : data.isf_filing?.cost_id
                  }
                : DEFAULT_CUSTOM_ISF_FILLING_CHARGES,
              warehousingCost: {
                value: data.warehousing_cost?.value,
                currency: data.warehousing_cost
                  ? CURRENCY?.find((item) => item.value === data.warehousing_cost?.currency)
                  : currency1,
                cost_id: isEdit == OPERATION_TYPE.DUBLICATE ? null : data.warehousing_cost?.cost_id
              },
              cif_price: {
                value: data?.cif_price?.value,
                currency: data.cif_price
                  ? CURRENCY?.find((item) => item.value === data?.cif_price?.currency)
                  : currency1,
                cost_id: isEdit == OPERATION_TYPE.DUBLICATE ? null : data.cif_price?.cost_id,
                manualOverride: data?.cif_price?.value ? true : false,
                weightUnit: data?.cif_price?.unit_of_weight
                  ? selectedWeightSystemChildOptions?.find(
                      (item: IWeightChildOption) => item.value === data?.cif_price?.unit_of_weight
                    )
                  : primaryUnit
              },
              cfr_price: {
                value: data?.cfr_price?.value,
                currency: data.cfr_price
                  ? CURRENCY?.find((item) => item.value === data?.cfr_price?.currency)
                  : currency1,
                cost_id: isEdit == OPERATION_TYPE.DUBLICATE ? null : data.cfr_price?.cost_id,
                manualOverride: data?.cfr_price?.value ? true : false,
                weightUnit: data?.cfr_price?.unit_of_weight
                  ? selectedWeightSystemChildOptions?.find(
                      (item: IWeightChildOption) => item.value === data?.cfr_price?.unit_of_weight
                    )
                  : primaryUnit
              },
              finalQuote_selling_price: {
                value: data?.final_price?.value,
                currency: data.final_price
                  ? CURRENCY?.find((item) => item.value === data?.final_price?.currency)
                  : currency1,
                cost_id: isEdit == OPERATION_TYPE.DUBLICATE ? null : data.final_price?.cost_id,
                weightUnit: data?.final_price?.unit_of_weight
                  ? selectedWeightSystemChildOptions?.find(
                      (item: IWeightChildOption) => item.value === data?.final_price?.unit_of_weight
                    )
                  : primaryUnit
              },
              moq: data.moq,
              moqUnit: data.moq_unit
                ? selectedWeightSystemChildOptions?.find(
                    (item: any) => item.value === data.moq_unit
                  )
                : primaryUnit,
              countryOfOrigin: getCountryOfOriginOption(data.origin, data?.origin_display_value),
              materialLocationCountry: getMaterialLocationCountryOption(
                data?.material_location_country,
                data?.material_location_country_display_value
              ),
              totalWeight: data.weight_one_container ?? 0,
              totalWeightUnit: data.weight_one_container_unit
                ? selectedWeightSystemChildOptions?.find(
                    (item: any) => item.value === data.weight_one_container_unit
                  )
                : primaryUnit,
              totalLocalFreight: {
                value: data.total_local_freight?.value,
                currency: data.total_local_freight
                  ? CURRENCY?.find((item) => item.value === data.total_local_freight?.currency)
                  : currency1,
                cost_id:
                  isEdit == OPERATION_TYPE.DUBLICATE ? null : data.total_local_freight?.cost_id
              },
              totalPortAndClearanceExpenses: {
                value: data.total_port_and_clearance_expenses?.value,
                currency: data.total_port_and_clearance_expenses
                  ? CURRENCY?.find(
                      (item) => item.value === data.total_port_and_clearance_expenses?.currency
                    )
                  : currency1,
                cost_id:
                  isEdit == OPERATION_TYPE.DUBLICATE
                    ? null
                    : data.total_port_and_clearance_expenses?.cost_id
              },
              totalFreight: {
                value: data.total_freight?.value,
                currency: data.total_freight
                  ? CURRENCY?.find((item) => item.value === data.total_freight?.currency)
                  : currency1,
                cost_id: isEdit == OPERATION_TYPE.DUBLICATE ? null : data.total_freight?.cost_id
              },
              portOfLoading: data.pol
                ? {
                    label: data.pol?.name,
                    value: data.pol?.id
                  }
                : null,
              portOfDischarge: data.pod
                ? {
                    label: data.pod?.name,
                    value: data.pod?.id
                  }
                : null,
              incoTermsLocation: data?.deal_incoterm_location,
              incoTermsZipCode: data?.zip_code,
              insuranceCost: data.insurance_cost ?? DEFAULT_INSURANCE_COST,
              additionalCost: {
                value: data.additional_cost?.value,
                currency: data.additional_cost
                  ? CURRENCY?.find((item) => item.value === data.additional_cost?.currency)
                  : currency1,
                cost_id: isEdit == OPERATION_TYPE.DUBLICATE ? null : data.additional_cost?.cost_id
              },
              financeCost: data.finance_cost,
              profitMarginExpected: data.profit_margin,
              discount: data.discount,
              tariff: data.tariff,
              incoTerms: data.deal_incoterm
                ? {
                    label: data.deal_incoterm,
                    value: data.deal_incoterm
                  }
                : {
                    label: 'CIF',
                    value: 'CIF'
                  },
              switchBlCost: {
                value: data.switch_bl_cost?.value,
                currency: data.switch_bl_cost
                  ? CURRENCY?.find((item) => item.value === data.switch_bl_cost?.currency)
                  : currency1,
                cost_id: isEdit == OPERATION_TYPE.DUBLICATE ? null : data.switch_bl_cost?.cost_id
              }
            }
          ]
        });
      }
    }
  }, []);

  const quoteWatch = useWatch({
    control,
    name: 'quote'
  });

  return (
    <form noValidate onSubmit={handleSubmit(onFormSubmit, notifyFirstFormError)}>
      <FormProvider {...quoteForm}>
        <CostCalculator
          filters={filterState}
          weightOptions={selectedWeightSystemChildOptions}
          currency={currency1}
          primaryUnitWatch={primaryUnit}
          quote={quoteWatch}
        />
        {fields.map((quote: any, index: number) => {
          return (
            <NonConsolidationQuote
              key={index}
              index={index}
              isExpanded={`quote.${index}.isExpanded`}
              productName={`quote.${index}.productName`}
              productPackaging={`quote.${index}.productPackaging`}
              dealQuantity={`quote.${index}.dealQuantity`}
              dealQuantityUnit={`quote.${index}.dealQuantityUnit`}
              basePrice={`quote.${index}.base_price.value`}
              basePriceCurrency={`quote.${index}.base_price.currency`}
              fobPrice={`quote.${index}.fob_price.value`}
              fobPriceCurrency={`quote.${index}.fob_price.currency`}
              fobPriceManualOverride={`quote.${index}.fob_price.manualOverride`}
              ddpPrice={`quote.${index}.ddp_price.value`}
              ddpPriceCurrency={`quote.${index}.ddp_price.currency`}
              ddpPriceManualOverride={`quote.${index}.ddp_price.manualOverride`}
              ddpPriceWeightUnit={`quote.${index}.ddp_price.weightUnit`}
              moq={`quote.${index}.moq`}
              moqUnit={`quote.${index}.moqUnit`}
              containerSize={`quote.${index}.containerSize`}
              totalWeight={`quote.${index}.totalWeight`}
              totalWeightUnit={`quote.${index}.totalWeightUnit`}
              totalLocalFreight={`quote.${index}.totalLocalFreight.value`}
              totalLocalFreightCurrency={`quote.${index}.totalLocalFreight.currency`}
              totalPortAndClearanceExpenses={`quote.${index}.totalPortAndClearanceExpenses.value`}
              totalPortAndClearanceExpensesCurrency={`quote.${index}.totalPortAndClearanceExpenses.currency`}
              totalFreight={`quote.${index}.totalFreight.value`}
              totalFreightCurrency={`quote.${index}.totalFreight.currency`}
              portOfLoading={`quote.${index}.portOfLoading`}
              portOfDischarge={`quote.${index}.portOfDischarge`}
              insuranceCost={`quote.${index}.insuranceCost`}
              additionalCost={`quote.${index}.additionalCost.value`}
              additionalCostCurrency={`quote.${index}.additionalCost.currency`}
              financeCost={`quote.${index}.financeCost`}
              profitMarginExpected={`quote.${index}.profitMarginExpected`}
              discount={`quote.${index}.discount`}
              tariff={`quote.${index}.tariff`}
              incoTerms={`quote.${index}.incoTerms`}
              incoTermsLocation={`quote.${index}.incoTermsLocation`}
              incoTermsZipCode={`quote.${index}.incoTermsZipCode`}
              switchBlCost={`quote.${index}.switchBlCost.value`}
              switchBLCostCurrency={`quote.${index}.switchBlCost.currency`}
              cifPrice={`quote.${index}.cif_price.value`}
              cfrPrice={`quote.${index}.cfr_price.value`}
              cifPriceManualOverride={`quote.${index}.cif_price.manualOverride`}
              cfrPriceManualOverride={`quote.${index}.cfr_price.manualOverride`}
              exWorksPriceManualOverride={`quote.${index}.final_exWorks_price.manualOverride`}
              cifPriceCurrency={`quote.${index}.cif_price.currency`}
              cfrPriceCurrency={`quote.${index}.cfr_price.currency`}
              finalExWorksPrice={`quote.${index}.final_exWorks_price.value`}
              finalExWorksPriceCurrency={`quote.${index}.final_exWorks_price.currency`}
              finalQuoteSellingPrice={`quote.${index}.finalQuote_selling_price.value`}
              finalQuoteSellingPriceWeightUnit={`quote.${index}.finalQuote_selling_price.weightUnit`}
              countryOfOrigin={`quote.${index}.countryOfOrigin`}
              materialLocationCountry={`quote.${index}.materialLocationCountry`}
              filterState={filterState}
              selectedWeightSystemChildOptions={selectedWeightSystemChildOptions}
              selectedCurrency={selectedCurrency}
              filterProductOptions={filterProductOptions}
              productOptions={productOptions}
              filterPorts={filterPorts}
              portOfLoadingOptions={portOfLoadingOptions}
              portOfDischargeOptions={portOfDischargeOptions}
              primaryUnitWatch={primaryUnit}
              currency1Watch={currency1}
              fxRateWatch={fxRate}
              basePriceWeightUnit={`quote.${index}.base_price.weightUnit`}
              fobPriceWeightUnit={`quote.${index}.fob_price.weightUnit`}
              cifPriceWeightUnit={`quote.${index}.cif_price.weightUnit`}
              cfrPriceWeightUnit={`quote.${index}.cfr_price.weightUnit`}
              finalExWorksPriceWeightUnit={`quote.${index}.final_exWorks_price.weightUnit`}
              drayageCharge={`quote.${index}.drayageCharges.value`}
              drayageChargeCurrency={`quote.${index}.drayageCharges.currency`}
              isfFiling={`quote.${index}.isfFiling.value`}
              isfFilingCurrency={`quote.${index}.isfFiling.currency`}
              customClearance={`quote.${index}.customClearance`}
              warehousingCostPrice={`quote.${index}.warehousingCost.value`}
              warehousingCostCurrency={`quote.${index}.warehousingCost.currency`}
              disableProductName={isEdit === OPERATION_TYPE.ADD_PRODUCT ? false : true}
            />
          );
        })}
        <section className={css.actionButtons}>
          <Button variant="outlined-secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="contained" type="submit">
            {isEdit == OPERATION_TYPE.EDIT ? 'Update Quote' : 'Create Quote'}
          </Button>
        </section>
      </FormProvider>
    </form>
  );
};

export default NonConsolidated;
